import React, { FC, useState, FormEvent, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ErrorDisplay from './components/ErrorDisplay';
import api from './api';
import './App.css';

const CreateOrganizationPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState({
    name: '',
    summary: '',
    description: '',
    imageAlt: '',
    hidden: false,
    isTopLevel: false,
    image: null as File | null,
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormState(prevState => ({ ...prevState, [name]: checked }));
    } else {
      setFormState(prevState => ({ ...prevState, [name]: value }));
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFormState(prevState => ({ ...prevState, image: files[0] }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsSubmitting(true);
    setError(null);

    const formData = new FormData();
    
    // Add all form fields to FormData
    formData.append('name', formState.name);
    formData.append('summary', formState.summary);
    
    if (formState.description.trim()) {
      formData.append('description', formState.description);
    }
    
    if (formState.imageAlt.trim()) {
      formData.append('imageAlt', formState.imageAlt);
    }
    
    // formData.append('hidden', formState.hidden.toString());
    
    if (formState.image) {
      formData.append('image', formState.image);
    }

    try {
      const response = await api('/organizations', {
        method: 'POST',
        body: formData,
      });

      // Navigate to organizations page or to the specific organization
      navigate('/organization');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Create New Organization</h1>
      <form onSubmit={handleSubmit} className="event-creation-form">
        {error && (
          <ErrorDisplay 
            error={error}
            title="Failed to create organization"
            showReferenceId={true}
          />
        )}
        
        <div className="form-group">
          <label htmlFor="name">Organization Name</label>
          <input 
            type="text" 
            id="name" 
            name="name" 
            value={formState.name} 
            onChange={handleInputChange} 
            required 
          />
        </div>

        <div className="form-group">
          <label htmlFor="summary">Summary</label>
          <textarea 
            id="summary" 
            name="summary" 
            value={formState.summary} 
            onChange={handleInputChange} 
            placeholder="A brief summary of what this organization does"
            required 
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description (Optional)</label>
          <textarea 
            id="description" 
            name="description" 
            value={formState.description} 
            onChange={handleInputChange} 
            placeholder="A detailed description of the organization's mission, history, and goals"
          />
        </div>

        <div className="form-group">
          <label htmlFor="image">Organization Image/Logo (Optional)</label>
          <input 
            type="file" 
            id="image" 
            name="image" 
            accept="image/*" 
            onChange={handleFileChange} 
          />
        </div>

        <div className="form-group">
          <label htmlFor="imageAlt">Image Alt Text (Optional)</label>
          <input 
            type="text" 
            id="imageAlt" 
            name="imageAlt" 
            value={formState.imageAlt} 
            onChange={handleInputChange}
            placeholder="Describe the image for accessibility (e.g., 'Organization logo')"
          />
        </div>

        {/* <div className="form-group">
          <label>
            <input 
              type="checkbox" 
              name="hidden" 
              checked={formState.hidden} 
              onChange={handleInputChange} 
            />
            Hide from public view
          </label>
        </div> */}

        {/* <div className="form-group">
          <label>
            <input 
              type="checkbox" 
              name="isTopLevel" 
              checked={formState.isTopLevel} 
              onChange={handleInputChange} 
            />
            Top-level organization
          </label>
        </div> */}

        <button type="submit" className="button-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create Organization'}
        </button>
      </form>
    </div>
  );
};

export default CreateOrganizationPage; 